/**
 * Centralized formatting utilities for consistent display across the application
 * Single source of truth for all currency, percentage, number, and date formatting
 */

import {
  formatDateForDisplay as dateUtilsFormatDisplay,
  formatDateForTable as dateUtilsFormatTable,
} from './date-utils';

/**
 * Format currency values consistently
 * @param value - The numeric value to format
 * @param options - Optional formatting options
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: number,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    compact?: boolean;
  } = {}
): string => {
  const { minimumFractionDigits = 0, maximumFractionDigits = 0, compact = false } = options;

  if (compact && Math.abs(value) >= 1000000) {
    const millions = value / 1000000;
    return `${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })
      .format(millions)
      .replace(/\.0+$/, '')}M`;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

/**
 * Format percentage values consistently
 * @param value - The decimal value to format (0.1 = 10%)
 * @param options - Optional formatting options
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string => {
  const { minimumFractionDigits = 1, maximumFractionDigits = 1 } = options;

  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

/**
 * Format millage values consistently
 * Strips any currency formatting (e.g., "$7.50" -> "7.50")
 * @param value - The millage value to format (can be string with formatting or number)
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns Formatted millage string (plain number without currency symbol)
 */
export const formatMillage = (value: string | number, decimalPlaces: number = 2): string => {
  // Strip any non-numeric characters except decimal point and minus sign
  const cleanValue = typeof value === 'string' ? value.replace(/[^\d.-]/g, '') : value;
  const numValue = typeof cleanValue === 'string' ? parseFloat(cleanValue) : cleanValue;

  if (Number.isNaN(numValue)) {
    return '0.00';
  }

  return numValue.toFixed(decimalPlaces);
};

/**
 * Format plain numbers with commas
 * @param value - The numeric value to format
 * @param options - Optional formatting options
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string => {
  const { minimumFractionDigits = 0, maximumFractionDigits = 0 } = options;

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

/**
 * Format date for display (MM/DD/YYYY)
 * Delegates to date-utils for consistency
 */
export const formatDateForDisplay = dateUtilsFormatDisplay;

/**
 * Format date for table display (e.g., "Jan 15, 2024")
 * Delegates to date-utils for consistency
 */
export const formatDateForTable = dateUtilsFormatTable;

/**
 * Format date using native toLocaleDateString
 * For cases where more control over formatting is needed
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format large numbers in compact notation (1.2M, 3.5K, etc.)
 * @param value - The numeric value to format
 * @param decimalPlaces - Number of decimal places in compact notation
 * @returns Formatted compact number string
 */
export const formatCompactNumber = (value: number, decimalPlaces: number = 1): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1e9) {
    return `${sign + (absValue / 1e9).toFixed(decimalPlaces)}B`;
  } else if (absValue >= 1e6) {
    return `${sign + (absValue / 1e6).toFixed(decimalPlaces)}M`;
  } else if (absValue >= 1e3) {
    return `${sign + (absValue / 1e3).toFixed(decimalPlaces)}K`;
  }

  return sign + absValue.toString();
};

/**
 * Format interest rate as percentage
 * Handles both decimal (0.05) and percentage (5) inputs
 * @param rate - The interest rate value
 * @param isDecimal - Whether the input is in decimal form (default: true)
 * @returns Formatted percentage string
 */
export const formatInterestRate = (rate: number, isDecimal: boolean = true): string => {
  const decimalRate = isDecimal ? rate : rate / 100;
  return formatPercentage(decimalRate, { minimumFractionDigits: 2, maximumFractionDigits: 3 });
};
